import { useState, useEffect, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Container, Row, Col, Tab, Nav } from "react-bootstrap";
import {
  FiUser,
  FiFileText,
  FiLock,
  FiBell,
  FiCamera,
  FiSave,
  FiEye,
  FiEyeOff,
  FiUpload,
  FiSettings,
  FiTruck,
  FiMapPin,
  FiGlobe,
} from "react-icons/fi";
import axios from "axios";
import Swal from "sweetalert2";
import MyContext from "../../../context/MyContext";
import "../../../assets/css/delivery/setting/DeliverySetting.css";
import DeliveryLayout from "../../../layouts/DeliveryPanelLayout";

const PAKISTAN_CITIES = [
  "Abbottabad",
  "Attock",
  "Bahawalpur",
  "Bahawalnagar",
  "Burewala",
  "Chakwal",
  "Chiniot",
  "Chishtian",
  "Daska",
  "Dera Ghazi Khan",
  "Dera Ismail Khan",
  "Faisalabad",
  "Ghotki",
  "Gujranwala",
  "Gujrat",
  "Hafizabad",
  "Haripur",
  "Hyderabad",
  "Islamabad",
  "Jacobabad",
  "Jhelum",
  "Kamoke",
  "Karachi",
  "Kasur",
  "Khanewal",
  "Khushab",
  "Khuzdar",
  "Lahore",
  "Larkana",
  "Layyah",
  "Lodhran",
  "Mailsi",
  "Mansehra",
  "Mardan",
  "Mianwali",
  "Mirpur Khas",
  "Multan",
  "Muridke",
  "Muzaffarabad",
  "Muzaffargarh",
  "Narowal",
  "Nawabshah",
  "Nowshera",
  "Okara",
  "Pakpattan",
  "Peshawar",
  "Quetta",
  "Rahim Yar Khan",
  "Rawalpindi",
  "Sadiqabad",
  "Sahiwal",
  "Sargodha",
  "Sheikhupura",
  "Sialkot",
  "Sukkur",
  "Swabi",
  "Swat",
  "Toba Tek Singh",
  "Vehari",
  "Wah Cantonment",
  "Wazirabad",
].sort();

function DeliverySettings() {
  const {
    baseUrl,
    baseUrlForFiles,
    loggedInDeliveryMan,
    authToken,
    regExpChecker,
  } = useContext(MyContext);

  const location = useLocation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(
    location.state?.openTab || "profile",
  );
  const [loading, setLoading] = useState(false);

  // ── Profile states ──
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [nameError, setNameError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [cityError, setCityError] = useState("");

  const [serviceCities, setServiceCities] = useState([]);
  const [savingCitySettings, setSavingCitySettings] = useState(false);
  const [citySearchTerm, setCitySearchTerm] = useState("");

  // ── KYC states ──
  const [kycStatus, setKycStatus] = useState("pending");
  const [cnic, setCnic] = useState("");
  const [vehicleType, setVehicleType] = useState("");
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [kycImage, setKycImage] = useState(null);
  const [kycImagePreview, setKycImagePreview] = useState("");
  const [kycPdf, setKycPdf] = useState(null);
  const [kycFileName, setKycFileName] = useState("");
  const [cnicError, setCnicError] = useState("");
  const [vehicleTypeError, setVehicleTypeError] = useState("");
  const [vehicleNumberError, setVehicleNumberError] = useState("");
  const [kycPdfError, setKycPdfError] = useState("");
  const [kycImageError, setKycImageError] = useState("");

  // ── Vehicle rate reference data (admin-controlled range) ──
  const [vehicleRatesList, setVehicleRatesList] = useState([]);
  const [selectedVehicleInfo, setSelectedVehicleInfo] = useState(null);

  // ── Driver's own vehicle capacity (must fall within admin's range) ──
  const [minCapacityKg, setMinCapacityKg] = useState("");
  const [maxCapacityKg, setMaxCapacityKg] = useState("");
  const [capacityError, setCapacityError] = useState("");

  // ── Account tab states ──
  const [currentPass, setCurrentPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [showCurrentPass, setShowCurrentPass] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [currentPassError, setCurrentPassError] = useState("");
  const [newPassError, setNewPassError] = useState("");
  const [confirmPassError, setConfirmPassError] = useState("");

  // ── Notification states ──
  const [inAppNotification, setInAppNotification] = useState(true);
  const [emailNotification, setEmailNotification] = useState(true);

  // ══════════════════════════════════════════════════════
  // HELPERS
  // ══════════════════════════════════════════════════════
  const formatVehicleTypeLabel = (type) => {
    if (!type) return "";
    return type
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // ══════════════════════════════════════════════════════
  // API — viewAllVehicleTypes (read-only reference list, admin-controlled)
  // ══════════════════════════════════════════════════════
  const viewAllVehicleTypes = () => {
    axios
      .get(`${baseUrl}/delivery/vehicle/viewAllVehicles`)
      .then((response) => {
        if (response.data.hasOwnProperty("error")) {
          console.log(response.data.error);
        } else {
          setVehicleRatesList(response.data.vehicle);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    viewAllVehicleTypes();
  }, []);

  // Whenever the driver picks a vehicle type, look up its admin-set
  // capacity range — display-only, never editable by the driver.
  useEffect(() => {
    if (!vehicleType || vehicleRatesList.length === 0) {
      setSelectedVehicleInfo(null);
      return;
    }
    const match = vehicleRatesList.find((v) => v.vehicle_type === vehicleType);
    setSelectedVehicleInfo(match || null);
  }, [vehicleType, vehicleRatesList]);

  // ══════════════════════════════════════════════════════
  // API — viewProfile
  // ══════════════════════════════════════════════════════
  const viewProfile = () => {
    if (!authToken) {
      setLoading(false);
      navigate("/delivery/login");
      return;
    }
    if (!loggedInDeliveryMan?.id) return;
    setLoading(true);

    axios
      .get(`${baseUrl}/delivery/profile/viewProfile`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        setLoading(false);
        if (response.data.hasOwnProperty("error")) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          const d = response.data.deliveryman;
          setName(d.name);
          setPhone(d.phone);
          setEmail(d.email);
          setCity(d.city);
          setAddress(d.address);
          setServiceCities(
            Array.isArray(d.service_cities) ? d.service_cities : [],
          );
          setPhotoPreview(d.image ? `${baseUrlForFiles}/${d.image}` : "");
          setCnic(d.cnic);
          setVehicleType(d.vehicle_type);
          setVehicleNumber(d.vehicle_number);
          setMinCapacityKg(d.vehicle_capacity_min || "");
          setMaxCapacityKg(d.vehicle_capacity_max || "");
          setKycStatus(d.is_verified || "pending");
          if (d.image) setKycImagePreview(`${baseUrlForFiles}/${d.image}`);
        }
      })
      .catch((error) => {
        setLoading(false);
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load profile.",
        });
      });
  };

  useEffect(() => {
    viewProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedInDeliveryMan?.id, authToken]);

  // ══════════════════════════════════════════════════════
  // API — updateProfile
  // ══════════════════════════════════════════════════════
  const updateProfile = () => {
    let valid = true;
    const nameErr = regExpChecker("username", name);
    const phoneErr = regExpChecker("phoneNumber", phone);
    if (nameErr) {
      setNameError(nameErr);
      valid = false;
    } else setNameError("");
    if (phoneErr) {
      setPhoneError(phoneErr);
      valid = false;
    } else setPhoneError("");
    if (!city.trim()) {
      setCityError("City is required");
      valid = false;
    } else setCityError("");
    if (!valid) return;

    setLoading(true);
    const formData = new FormData();
    formData.append("name", name);
    formData.append("phone", phone);
    formData.append("city", city);
    if (photo) formData.append("image", photo);

    axios
      .post(`${baseUrl}/delivery/profile/updateProfile`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        setLoading(false);
        if (response.data.success) {
          Swal.fire({
            icon: "success",
            title: "Saved!",
            text: "Profile updated successfully",
            confirmButtonColor: "#097d43",
          });
          viewProfile();
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.message,
          });
        }
      })
      .catch((error) => {
        setLoading(false);
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to update profile.",
        });
      });
  };

  // ══════════════════════════════════════════════════════
  // API — updateCitySettings
  // Same "/delivery/profile/updateProfile" endpoint reuse karta hai
  // (generic mass-update). Pehle sirf service_cities bhejta tha —
  // FIXED: ab "city" (pickup city) bhi isi call ke sath bhejta hai,
  // kyunke ab ye tab hi Pickup City select karne ki jagah hai (dropdown
  // neeche, OpenJobs.js ki "Change Current City" wali cities-list ki
  // tarah), Profile tab pe jane ki zaroorat nahi rahi.
  // ══════════════════════════════════════════════════════
  const updateCitySettings = () => {
    if (!city) {
      setCityError("Please select your pickup city");
      return;
    }
    setCityError("");
    setSavingCitySettings(true);
    const formData = new FormData();

    formData.append("city", city);
    // ── service_cities[] — Laravel isay khud PHP array ki tarah parse
    // kar leta hai, is liye JSON.stringify nahi karna — model ke array
    // cast ko seedha array chahiye ──
    serviceCities.forEach((c) => formData.append("service_cities[]", c));

    axios
      .post(`${baseUrl}/delivery/profile/updateProfile`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        setSavingCitySettings(false);
        if (response.data.success) {
          Swal.fire({
            icon: "success",
            title: "Saved!",
            text: "Your pickup city and service cities have been updated.",
            confirmButtonColor: "#097d43",
          });
          viewProfile();
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.message,
          });
        }
      })
      .catch((error) => {
        setSavingCitySettings(false);
        if (error.response?.status === 401) {
          window.location.href = "/delivery/login";
          return;
        }
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to update city settings.",
        });
      });
  };

  const toggleServiceCity = (cityName) => {
    setServiceCities((prev) =>
      prev.includes(cityName)
        ? prev.filter((c) => c !== cityName)
        : [...prev, cityName],
    );
  };

  const visibleServiceCities = PAKISTAN_CITIES.filter((c) =>
    c.toLowerCase().includes(citySearchTerm.toLowerCase()),
  );

  // ══════════════════════════════════════════════════════
  // API — submitKyc
  // ══════════════════════════════════════════════════════
  const submitKyc = () => {
    let valid = true;
    const cnicErr = regExpChecker("CNICnumber", cnic);
    if (cnicErr) {
      setCnicError(cnicErr);
      valid = false;
    } else setCnicError("");
    if (!vehicleType) {
      setVehicleTypeError("Vehicle type is required");
      valid = false;
    } else setVehicleTypeError("");
    if (!vehicleNumber.trim()) {
      setVehicleNumberError("Vehicle number is required");
      valid = false;
    } else setVehicleNumberError("");

    // Capacity must be provided and must fall within the admin-set range
    // for the selected vehicle type.
    if (!minCapacityKg || !maxCapacityKg) {
      setCapacityError("Please enter your vehicle's min and max capacity.");
      valid = false;
    } else if (Number(minCapacityKg) > Number(maxCapacityKg)) {
      setCapacityError("Min capacity cannot be greater than max capacity.");
      valid = false;
    } else if (
      selectedVehicleInfo &&
      (Number(minCapacityKg) < Number(selectedVehicleInfo.min_capacity_kg) ||
        Number(maxCapacityKg) > Number(selectedVehicleInfo.max_capacity_kg))
    ) {
      setCapacityError(
        `Capacity must be between ${selectedVehicleInfo.min_capacity_kg} kg and ${selectedVehicleInfo.max_capacity_kg} kg for this vehicle type.`,
      );
      valid = false;
    } else {
      setCapacityError("");
    }

    if (!kycPdf) {
      setKycPdfError("Please upload your KYC documents PDF");
      valid = false;
    } else setKycPdfError("");

    // ── profile photo ab required hai — customer ko delivery se
    // pehle pata hona chahiye kaun deliver karne aa raha hai ──
    if (!kycImage) {
      setKycImageError("Please upload your profile photo");
      valid = false;
    } else setKycImageError("");

    if (!valid) return;

    setLoading(true);
    const formData = new FormData();
    formData.append("cnic", cnic);
    formData.append("vehicle_type", vehicleType);
    formData.append("vehicle_number", vehicleNumber);
    formData.append("vehicle_capacity_min", minCapacityKg);
    formData.append("vehicle_capacity_max", maxCapacityKg);
    formData.append("documents", kycPdf);
    formData.append("image", kycImage);

    axios
      .post(`${baseUrl}/delivery/kyc/submitKyc`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        setLoading(false);
        if (response.data.success) {
          setKycStatus("pending");
          Swal.fire({
            icon: "success",
            title: "Submitted!",
            text: "KYC submitted successfully. Admin will review your documents.",
            confirmButtonColor: "#097d43",
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.message,
          });
        }
      })
      .catch((error) => {
        setLoading(false);
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to submit KYC.",
        });
      });
  };

  // ══════════════════════════════════════════════════════
  // API — changePassword
  // ══════════════════════════════════════════════════════
  const changePassword = () => {
    let valid = true;
    const currPassErr = regExpChecker("password", currentPass);
    const newPassErr = regExpChecker("password", newPass);
    if (currPassErr) {
      setCurrentPassError(currPassErr);
      valid = false;
    } else setCurrentPassError("");
    if (newPassErr) {
      setNewPassError(newPassErr);
      valid = false;
    } else setNewPassError("");
    if (!confirmPass) {
      setConfirmPassError("Please confirm password");
      valid = false;
    } else if (confirmPass !== newPass) {
      setConfirmPassError("Passwords do not match");
      valid = false;
    } else setConfirmPassError("");
    if (!valid) return;

    Swal.fire({
      title: "Change Password?",
      text: "You will need to login again with your new password",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#097d43",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, Change Password",
    }).then((result) => {
      if (result.isConfirmed) {
        setLoading(true);
        const formData = new FormData();
        formData.append("current_password", currentPass);
        formData.append("new_password", newPass);

        axios
          .post(`${baseUrl}/delivery/profile/updatePassword`, formData, {
            headers: { Authorization: `Bearer ${authToken}` },
          })
          .then((response) => {
            setLoading(false);
            if (response.data.success) {
              Swal.fire({
                icon: "success",
                title: "Changed!",
                text: "Password changed successfully",
                confirmButtonColor: "#097d43",
              });
              setCurrentPass("");
              setNewPass("");
              setConfirmPass("");
            } else {
              Swal.fire({
                icon: "error",
                title: "Error",
                text: response.data.message,
              });
            }
          })
          .catch((error) => {
            setLoading(false);
            console.log(error);
            Swal.fire({
              icon: "error",
              title: "Error",
              text: "Failed to change password.",
            });
          });
      }
    });
  };

  // ══════════════════════════════════════════════════════
  // HELPERS
  // ══════════════════════════════════════════════════════
  const getKycBadge = (status) => {
    const map = {
      pending: { cls: "dst-kyc-pending", label: "Verification Pending" },
      approved: { cls: "dst-kyc-approved", label: "Verified" },
      rejected: { cls: "dst-kyc-rejected", label: "Rejected" },
    };
    const s = map[status] || map["pending"];
    return <span className={s.cls}>{s.label}</span>;
  };

  const Toggle = ({ value, onToggle }) => (
    <div
      className={`dst-toggle ${value ? "dst-toggle-on" : "dst-toggle-off"}`}
      onClick={onToggle}
    >
      <div className="dst-toggle-knob" />
    </div>
  );

  // ══════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════
  return (
    <DeliveryLayout>
      <div className="dst-wrapper">
        <Container fluid="lg">
          {/* ── Page Title ── */}
          <div className="dst-title">
            <h3>Settings</h3>
            <p>Manage your profile, documents and preferences</p>
          </div>

          <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
            {/* ── Tab Nav ── */}
            <div className="dst-tabs-card">
              <Nav className="dst-nav">
                <Nav.Item>
                  <Nav.Link eventKey="profile" className="dst-nav-link">
                    <FiUser size={16} /> Profile
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="cities" className="dst-nav-link">
                    <FiGlobe size={16} /> Delivery Cities
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="kyc" className="dst-nav-link">
                    <FiFileText size={16} /> KYC
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="account" className="dst-nav-link">
                    <FiSettings size={16} /> Account
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="notifications" className="dst-nav-link">
                    <FiBell size={16} /> Notifications
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </div>

            <Tab.Content className="dst-tab-content">
              {/* ══ TAB 1 — PROFILE ══ */}
              <Tab.Pane eventKey="profile">
                <div className="dst-section-card">
                  <div className="dst-section-card-header">
                    <h5 className="dst-section-heading">Profile Information</h5>
                    <button
                      className="dst-save-btn"
                      onClick={updateProfile}
                      disabled={loading}
                    >
                      {loading ? (
                        "Saving..."
                      ) : (
                        <>
                          <FiSave size={15} /> Save Profile
                        </>
                      )}
                    </button>
                  </div>

                  {/* Photo */}
                  <div className="dst-photo-section">
                    <div className="dst-photo-wrap">
                      {photoPreview ? (
                        <img
                          src={photoPreview}
                          alt="profile"
                          className="dst-photo-img"
                        />
                      ) : (
                        <div className="dst-photo-placeholder">
                          <FiUser size={36} />
                        </div>
                      )}
                      <label className="dst-photo-edit">
                        <input
                          type="file"
                          accept="image/*"
                          style={{ display: "none" }}
                          onChange={(e) => {
                            const f = e.target.files[0];
                            if (!f) return;
                            setPhoto(f);
                            setPhotoPreview(URL.createObjectURL(f));
                          }}
                        />
                        <FiCamera size={14} />
                      </label>
                    </div>
                    <div>
                      <p className="dst-photo-name">{name || "Your Name"}</p>
                      <p className="dst-photo-role">Delivery Boy</p>
                    </div>
                  </div>

                  <hr className="dst-divider" />

                  <Row className="g-3">
                    <Col xs={12} md={6}>
                      <div className="dst-form-group">
                        <label className="dst-form-label">Full Name</label>
                        <input
                          className={`dst-form-input ${nameError ? "dst-input-error" : ""}`}
                          value={name}
                          placeholder="Enter full name"
                          onChange={(e) => {
                            setName(e.target.value);
                            setNameError(
                              regExpChecker("username", e.target.value),
                            );
                          }}
                        />
                        {nameError && (
                          <p className="dst-error-msg">{nameError}</p>
                        )}
                      </div>
                    </Col>
                    <Col xs={12} md={6}>
                      <div className="dst-form-group">
                        <label className="dst-form-label">Phone Number</label>
                        <input
                          className={`dst-form-input ${phoneError ? "dst-input-error" : ""}`}
                          value={phone}
                          placeholder="03XXXXXXXXX"
                          onChange={(e) => {
                            setPhone(e.target.value);
                            setPhoneError(
                              regExpChecker("phoneNumber", e.target.value),
                            );
                          }}
                        />
                        {phoneError && (
                          <p className="dst-error-msg">{phoneError}</p>
                        )}
                      </div>
                    </Col>
                    <Col xs={12} md={6}>
                      <div className="dst-form-group">
                        <label className="dst-form-label">City</label>
                        <input
                          className={`dst-form-input ${cityError ? "dst-input-error" : ""}`}
                          value={city}
                          placeholder="e.g. Lahore"
                          onChange={(e) => {
                            setCity(e.target.value);
                            if (!e.target.value.trim())
                              setCityError("City is required");
                            else setCityError("");
                          }}
                        />
                        {cityError && (
                          <p className="dst-error-msg">{cityError}</p>
                        )}
                      </div>
                    </Col>
                  </Row>
                </div>
              </Tab.Pane>

              {/* ══ TAB 2 — DELIVERY CITIES ══
                  Intercity Bulk Delivery Module (Section 5) — apna
                  alag tab, Profile se separate. ══ */}
              <Tab.Pane eventKey="cities">
                <div className="dst-section-card">
                  <div className="dst-section-card-header">
                    <div>
                      <h5 className="dst-section-heading">
                        <FiTruck size={16} className="me-1" /> Delivery City
                        Settings
                      </h5>
                      <p className="dst-kyc-note" style={{ marginTop: 4 }}>
                        Ye batata hai aap abhi kahan se operate kar rahay hain,
                        aur kin shehron ke liye intercity delivery lena
                        chahenge. KYC se alag hai — jab chahen update karein.
                      </p>
                    </div>
                    <button
                      className="dst-save-btn"
                      onClick={updateCitySettings}
                      disabled={savingCitySettings}
                    >
                      {savingCitySettings ? (
                        "Saving..."
                      ) : (
                        <>
                          <FiSave size={15} /> Save City Settings
                        </>
                      )}
                    </button>
                  </div>

                  {/* ── NEW: current city + selected service cities ka
                      summary — OpenJobs.js ke "city bar" jaisa hi, isi
                      page par turant dikhta hai (bina Open Jobs par
                      jaaye) ── */}
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 10,
                      background: "#f8f9fa",
                      border: "1px solid #eee",
                      borderRadius: 10,
                      padding: "12px 16px",
                      marginBottom: 16,
                      fontSize: 13.5,
                    }}
                  >
                    <div>
                      <p style={{ margin: 0 }}>
                        <FiMapPin size={13} className="me-1" />{" "}
                        <strong>Current City:</strong> {city || "Not set"}
                      </p>
                      <p style={{ margin: "4px 0 0", color: "#6c757d" }}>
                        You provide services in:{" "}
                        {serviceCities.length > 0
                          ? serviceCities.join(", ")
                          : "No service cities added yet"}
                      </p>
                    </div>
                  </div>

                  <Row className="g-3">
                    <Col xs={12} md={6}>
                      <div className="dst-form-group">
                        <label className="dst-form-label">
                          <FiMapPin size={13} className="me-1" /> Your Pickup
                          City
                        </label>
                        {/* FIXED: pehle ye field disabled input thi aur sirf
                            "Profile" tab se badalti thi. Ab OpenJobs.js ki
                            "Change Current City" jaisi hi ek dropdown hai —
                            yahin se seedha select/change ho sakti hai.
                            FIXED (2): ab poori Pakistan list ki bajaye
                            sirf unhi cities dikhata hai jo neeche "Service
                            Cities" mein select ki gayi hain — bilkul
                            OpenJobs.js ke "Change Current City" modal jaisa
                            (agar maujooda city kisi wajah se service
                            cities mein na ho, usay bhi list mein rakha hai
                            taake selection achanak ghayab na ho jaye). */}
                        <select
                          className={`dst-form-input ${cityError ? "dst-input-error" : ""}`}
                          value={city}
                          disabled={serviceCities.length === 0 && !city}
                          onChange={(e) => {
                            setCity(e.target.value);
                            setCityError(
                              e.target.value ? "" : "City is required",
                            );
                          }}
                        >
                          <option value="">Select your city</option>
                          {(city && !serviceCities.includes(city)
                            ? [city, ...serviceCities]
                            : serviceCities
                          ).map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                        {cityError && (
                          <p className="dst-error-msg">{cityError}</p>
                        )}
                        <small
                          style={{
                            color: "#999",
                            fontSize: "0.78rem",
                            display: "block",
                            marginTop: 4,
                          }}
                        >
                          {serviceCities.length === 0
                            ? 'Pehle neeche "Service Cities" mein se apni cities select karein, phir yahan se pickup city choose kar sakte hain.'
                            : 'Jobs sirf isi city ke sellers se milengi (pickup point). "Save City Settings" dabate hi ye seedha update ho jaegi.'}
                        </small>
                      </div>
                    </Col>
                  </Row>

                  <hr className="dst-divider" />

                  <div className="dst-form-group">
                    <label className="dst-form-label">
                      <FiGlobe size={13} className="me-1" /> Service Cities
                      (Intercity Delivery)
                    </label>
                    <small
                      style={{
                        color: "#999",
                        fontSize: "0.78rem",
                        display: "block",
                        marginBottom: 8,
                      }}
                    >
                      In shehron mein select karein jahan aap bulk/intercity
                      orders deliver karne ko willing hain. Current city ke
                      elawa aur bhi cities select ki ja sakti hain.
                    </small>

                    <input
                      type="text"
                      className="dst-form-input"
                      placeholder="Search cities..."
                      value={citySearchTerm}
                      onChange={(e) => setCitySearchTerm(e.target.value)}
                      style={{ marginBottom: 10 }}
                    />

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fill, minmax(150px, 1fr))",
                        gap: "8px",
                        maxHeight: 240,
                        overflowY: "auto",
                        border: "1px solid #e5e7eb",
                        borderRadius: 8,
                        padding: 12,
                      }}
                    >
                      {visibleServiceCities.map((c) => (
                        <label
                          key={c}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            fontSize: 13,
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={serviceCities.includes(c)}
                            onChange={() => toggleServiceCity(c)}
                          />
                          {c}
                        </label>
                      ))}
                      {visibleServiceCities.length === 0 && (
                        <span style={{ fontSize: 13, color: "#999" }}>
                          No city found
                        </span>
                      )}
                    </div>

                    {serviceCities.length > 0 && (
                      <div
                        style={{
                          marginTop: 10,
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 6,
                        }}
                      >
                        {serviceCities.map((c) => (
                          <span
                            key={c}
                            style={{
                              fontSize: 12,
                              background: "#e8f5f0",
                              color: "#097d43",
                              padding: "4px 10px",
                              borderRadius: 20,
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                            }}
                          >
                            {c}
                            <span
                              style={{ cursor: "pointer", fontWeight: 700 }}
                              onClick={() => toggleServiceCity(c)}
                            >
                              ×
                            </span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </Tab.Pane>

              {/* ══ TAB 3 — KYC ══ */}
              <Tab.Pane eventKey="kyc">
                <div className="dst-section-card">
                  <div className="dst-section-card-header">
                    <div>
                      <h5 className="dst-section-heading">KYC Verification</h5>
                      {getKycBadge(kycStatus)}
                    </div>
                    <button
                      className="dst-save-btn"
                      onClick={submitKyc}
                      disabled={loading || kycStatus === "approved"}
                    >
                      {loading ? (
                        "Submitting..."
                      ) : (
                        <>
                          <FiUpload size={15} /> Submit KYC
                        </>
                      )}
                    </button>
                  </div>

                  <p className="dst-kyc-note">
                    Fill in your vehicle details and upload your documents PDF
                    for verification.
                  </p>

                  <div className="dst-kyc-req-box">
                    <p className="dst-kyc-req-title">
                      Required Documents in PDF:
                    </p>
                    <ul className="dst-kyc-req-list">
                      <li>✅ CNIC Front</li>
                      <li>✅ CNIC Back</li>
                      <li>✅ Driving License</li>
                      <li>✅ Vehicle Registration</li>
                    </ul>
                  </div>

                  <Row className="g-3 mb-3">
                    <Col xs={12} md={6}>
                      <div className="dst-form-group">
                        <label className="dst-form-label">CNIC Number</label>
                        <input
                          className={`dst-form-input ${cnicError ? "dst-input-error" : ""}`}
                          value={cnic}
                          placeholder="13-digit CNIC (with dashes)"
                          maxLength={15}
                          onChange={(e) => {
                            setCnic(e.target.value);
                            setCnicError(
                              regExpChecker("CNICnumber", e.target.value),
                            );
                          }}
                        />
                        {cnicError && (
                          <p className="dst-error-msg">{cnicError}</p>
                        )}
                      </div>
                    </Col>
                    <Col xs={12} md={6}>
                      <div className="dst-form-group">
                        <label className="dst-form-label">Vehicle Type</label>
                        <select
                          className={`dst-form-input ${vehicleTypeError ? "dst-input-error" : ""}`}
                          value={vehicleType}
                          onChange={(e) => {
                            setVehicleType(e.target.value);
                            if (!e.target.value)
                              setVehicleTypeError("Vehicle type is required");
                            else setVehicleTypeError("");
                          }}
                        >
                          <option value="">Select vehicle</option>
                          {vehicleRatesList.map((v) => (
                            <option key={v.id} value={v.vehicle_type}>
                              {formatVehicleTypeLabel(v.vehicle_type)}
                            </option>
                          ))}
                        </select>
                        {vehicleTypeError && (
                          <p className="dst-error-msg">{vehicleTypeError}</p>
                        )}
                      </div>
                    </Col>
                    <Col xs={12} md={6}>
                      <div className="dst-form-group">
                        <label className="dst-form-label">Vehicle Number</label>
                        <input
                          className={`dst-form-input ${vehicleNumberError ? "dst-input-error" : ""}`}
                          value={vehicleNumber}
                          placeholder="e.g. LHR-1234"
                          onChange={(e) => {
                            setVehicleNumber(e.target.value);
                            if (!e.target.value.trim())
                              setVehicleNumberError(
                                "Vehicle number is required",
                              );
                            else setVehicleNumberError("");
                          }}
                        />
                        {vehicleNumberError && (
                          <p className="dst-error-msg">{vehicleNumberError}</p>
                        )}
                      </div>
                    </Col>

                    {/* Admin-set capacity range — read-only reference, shown
                        only after a vehicle type is picked */}
                    {selectedVehicleInfo && (
                      <Col xs={12}>
                        <div className="dst-vehicle-info-box">
                          <p className="dst-vehicle-info-title">
                            <FiTruck size={14} /> Allowed Capacity Range for{" "}
                            {formatVehicleTypeLabel(
                              selectedVehicleInfo.vehicle_type,
                            )}
                          </p>
                          <div className="dst-vehicle-info-grid">
                            <div>
                              <span className="dst-vehicle-info-label">
                                Weight Range
                              </span>
                              <span className="dst-vehicle-info-value">
                                {selectedVehicleInfo.min_capacity_kg} –{" "}
                                {selectedVehicleInfo.max_capacity_kg} kg
                              </span>
                            </div>
                          </div>
                          <p className="dst-vehicle-info-note">
                            <FiLock size={11} /> Set by admin — your entered
                            capacity below must stay within this range
                          </p>
                        </div>
                      </Col>
                    )}

                    {/* Driver's own vehicle capacity — must fall inside the
                        admin range shown above (validated in submitKyc) */}
                    <Col xs={12} md={6}>
                      <div className="dst-form-group">
                        <label className="dst-form-label">
                          Your Vehicle's Min Capacity (kg)
                        </label>
                        <input
                          type="number"
                          min="0"
                          className={`dst-form-input ${capacityError ? "dst-input-error" : ""}`}
                          value={minCapacityKg}
                          placeholder="e.g. 50"
                          onChange={(e) => {
                            setMinCapacityKg(e.target.value);
                            setCapacityError("");
                          }}
                        />
                      </div>
                    </Col>
                    <Col xs={12} md={6}>
                      <div className="dst-form-group">
                        <label className="dst-form-label">
                          Your Vehicle's Max Capacity (kg)
                        </label>
                        <input
                          type="number"
                          min="0"
                          className={`dst-form-input ${capacityError ? "dst-input-error" : ""}`}
                          value={maxCapacityKg}
                          placeholder="e.g. 200"
                          onChange={(e) => {
                            setMaxCapacityKg(e.target.value);
                            setCapacityError("");
                          }}
                        />
                        {capacityError && (
                          <p className="dst-error-msg">{capacityError}</p>
                        )}
                      </div>
                    </Col>

                    <Col xs={12} md={6}>
                      <div className="dst-form-group">
                        <label className="dst-form-label">Profile Image</label>
                        <label className="dst-doc-upload">
                          <input
                            type="file"
                            accept="image/*"
                            style={{ display: "none" }}
                            onChange={(e) => {
                              const f = e.target.files[0];
                              if (!f) return;
                              setKycImage(f);
                              setKycImagePreview(URL.createObjectURL(f));
                            }}
                          />
                          {kycImagePreview ? (
                            <img
                              src={kycImagePreview}
                              alt="profile"
                              className="dst-doc-preview"
                            />
                          ) : (
                            <div className="dst-doc-placeholder">
                              <FiUpload size={22} />
                              <p>Upload Profile Photo</p>
                              <span>JPG, PNG supported</span>
                            </div>
                          )}
                        </label>
                      </div>
                    </Col>
                  </Row>

                  <div className="dst-form-group">
                    <label className="dst-form-label">
                      KYC Documents (PDF)
                    </label>
                    <label
                      className={`dst-pdf-upload ${kycPdfError ? "dst-pdf-upload-error" : ""}`}
                    >
                      <input
                        type="file"
                        accept="application/pdf"
                        style={{ display: "none" }}
                        onChange={(e) => {
                          const f = e.target.files[0];
                          if (!f) return;
                          setKycPdf(f);
                          setKycFileName(f.name);
                          setKycPdfError("");
                        }}
                      />
                      {kycPdf ? (
                        <div className="dst-pdf-selected">
                          <FiFileText size={32} />
                          <div>
                            <p className="dst-pdf-name">{kycFileName}</p>
                            <span className="dst-pdf-size">
                              {(kycPdf.size / 1024).toFixed(1)} KB — Click to
                              change
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="dst-doc-placeholder">
                          <FiUpload size={28} />
                          <p>Click to upload KYC PDF</p>
                          <span>
                            Merge all documents into one PDF before uploading
                          </span>
                        </div>
                      )}
                    </label>
                    {kycPdfError && (
                      <p className="dst-error-msg">{kycPdfError}</p>
                    )}
                  </div>
                </div>
              </Tab.Pane>

              {/* ══ TAB 4 — ACCOUNT ══ */}
              <Tab.Pane eventKey="account">
                <div className="dst-section-card">
                  {/* Change Password */}
                  <div className="dst-section-card-header">
                    <h5 className="dst-section-heading">Change Password</h5>
                    <p className="dst-kyc-note">
                      Keep your account secure with a strong password
                    </p>
                  </div>
                  <Row className="g-3">
                    <Col xs={12}>
                      <div className="dst-form-group">
                        <label className="dst-form-label">
                          Current Password
                        </label>
                        <div className="dst-pass-wrap">
                          <input
                            type={showCurrentPass ? "text" : "password"}
                            className={`dst-form-input dst-pass-input ${currentPassError ? "dst-input-error" : ""}`}
                            placeholder="Enter current password"
                            value={currentPass}
                            onChange={(e) => {
                              setCurrentPass(e.target.value);
                              setCurrentPassError(
                                regExpChecker("password", e.target.value),
                              );
                            }}
                          />
                          <button
                            className="dst-pass-eye"
                            onClick={() => setShowCurrentPass((p) => !p)}
                          >
                            {showCurrentPass ? (
                              <FiEyeOff size={16} />
                            ) : (
                              <FiEye size={16} />
                            )}
                          </button>
                        </div>
                        {currentPassError && (
                          <p className="dst-error-msg">{currentPassError}</p>
                        )}
                      </div>
                    </Col>
                    <Col xs={12} md={6}>
                      <div className="dst-form-group">
                        <label className="dst-form-label">New Password</label>
                        <div className="dst-pass-wrap">
                          <input
                            type={showNewPass ? "text" : "password"}
                            className={`dst-form-input dst-pass-input ${newPassError ? "dst-input-error" : ""}`}
                            placeholder="Min 8 characters"
                            value={newPass}
                            onChange={(e) => {
                              setNewPass(e.target.value);
                              setNewPassError(
                                regExpChecker("password", e.target.value),
                              );
                            }}
                          />
                          <button
                            className="dst-pass-eye"
                            onClick={() => setShowNewPass((p) => !p)}
                          >
                            {showNewPass ? (
                              <FiEyeOff size={16} />
                            ) : (
                              <FiEye size={16} />
                            )}
                          </button>
                        </div>
                        {newPassError && (
                          <p className="dst-error-msg">{newPassError}</p>
                        )}
                      </div>
                    </Col>
                    <Col xs={12} md={6}>
                      <div className="dst-form-group">
                        <label className="dst-form-label">
                          Confirm Password
                        </label>
                        <div className="dst-pass-wrap">
                          <input
                            type={showConfirmPass ? "text" : "password"}
                            className={`dst-form-input dst-pass-input ${confirmPassError ? "dst-input-error" : ""}`}
                            placeholder="Re-enter new password"
                            value={confirmPass}
                            onChange={(e) => {
                              setConfirmPass(e.target.value);
                              if (e.target.value !== newPass)
                                setConfirmPassError("Passwords do not match");
                              else setConfirmPassError("");
                            }}
                          />
                          <button
                            className="dst-pass-eye"
                            onClick={() => setShowConfirmPass((p) => !p)}
                          >
                            {showConfirmPass ? (
                              <FiEyeOff size={16} />
                            ) : (
                              <FiEye size={16} />
                            )}
                          </button>
                        </div>
                        {confirmPassError && (
                          <p className="dst-error-msg">{confirmPassError}</p>
                        )}
                      </div>
                    </Col>
                  </Row>

                  <div className="dst-pass-req-box">
                    <p className="dst-pass-req-title">Password Requirements:</p>
                    <ul className="dst-pass-req-list">
                      <li className={newPass.length >= 8 ? "dst-req-met" : ""}>
                        Minimum 8 characters long
                      </li>
                      <li
                        className={/[A-Z]/.test(newPass) ? "dst-req-met" : ""}
                      >
                        At least one uppercase letter
                      </li>
                      <li
                        className={/[a-z]/.test(newPass) ? "dst-req-met" : ""}
                      >
                        At least one lowercase letter
                      </li>
                      <li
                        className={/[0-9]/.test(newPass) ? "dst-req-met" : ""}
                      >
                        At least one number
                      </li>
                      <li
                        className={
                          /[@#$%^&*]/.test(newPass) ? "dst-req-met" : ""
                        }
                      >
                        At least one special character (@, #, $, etc.)
                      </li>
                    </ul>
                  </div>

                  <div className="dst-save-row">
                    <button
                      className="dst-cancel-pass-btn"
                      onClick={() => {
                        setCurrentPass("");
                        setNewPass("");
                        setConfirmPass("");
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className="dst-save-btn"
                      onClick={changePassword}
                      disabled={loading}
                    >
                      {loading ? (
                        "Saving..."
                      ) : (
                        <>
                          <FiLock size={15} /> Change Password
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </Tab.Pane>

              {/* ══ TAB 5 — NOTIFICATIONS ══ */}
              <Tab.Pane eventKey="notifications">
                <div className="dst-section-card">
                  <h5 className="dst-section-heading">
                    Notification Preferences
                  </h5>
                  <p className="dst-kyc-note">
                    Choose how you want to receive notifications.
                  </p>

                  <div className="dst-notif-row">
                    <div className="dst-notif-text">
                      <p className="dst-notif-label">In-App Notifications</p>
                      <p className="dst-notif-sub">
                        Receive notifications inside the app
                      </p>
                    </div>
                    <Toggle
                      value={inAppNotification}
                      onToggle={() => setInAppNotification((p) => !p)}
                    />
                  </div>

                  <div className="dst-notif-row">
                    <div className="dst-notif-text">
                      <p className="dst-notif-label">Email Notifications</p>
                      <p className="dst-notif-sub">
                        Receive notifications via email
                      </p>
                    </div>
                    <Toggle
                      value={emailNotification}
                      onToggle={() => setEmailNotification((p) => !p)}
                    />
                  </div>
                </div>
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </Container>
      </div>
    </DeliveryLayout>
  );
}

export default DeliverySettings;
